import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrandKitFontPickerContent } from './BrandKitFontPickerContent';
import { classnames } from '@/libs/utils';
import { useDynamicPopupPosition } from '@/hooks/useDynamicPopupPosition';
import Icon from '@/components/atoms/Icon';

interface BrandKitFontPickerProps {
  /** Current selection: font file URL (same as `font_location` in clip metadata). */
  selectedFontUrl: string;
  /** When several options share the same URL, disambiguate the active row (e.g. platform presets using one placeholder file). */
  selectedFontDisplayName?: string;
  onFontSelect: (fontUrl: string, fontDisplayName: string) => void;
  onClose?: () => void;
  /** Panel title shown next to the optional back control. */
  title?: string;
  children?: React.ReactNode;
  classNames?: string;
  openClassNames?: string;
  /** Extra classes applied to the floating panel div (e.g. positioning overrides). */
  panelClassNames?: string;
  useDynamicPosition?: boolean;
  /** Where to anchor the panel when useDynamicPosition is true. Defaults to 'right'. */
  preferredPosition?: 'left' | 'right' | 'top' | 'bottom';
  /**
   * When provided, clicking the trigger calls this instead of opening the floating panel.
   * Used by parent popovers to implement the panel-swap back-button pattern.
   */
  onOpen?: () => void;
  /** Render the picker content inline (no trigger div, no floating wrapper). */
  inline?: boolean;
  /** When inline is true, shows a ← button that calls this handler. */
  onBack?: () => void;
}

export default function BrandKitFontPicker({
  selectedFontUrl,
  selectedFontDisplayName,
  onFontSelect,
  onClose,
  title = 'Font Input',
  children,
  classNames = '',
  openClassNames = '',
  panelClassNames = '',
  useDynamicPosition = false,
  preferredPosition = 'right',
  onOpen,
  inline = false,
  onBack
}: BrandKitFontPickerProps): JSX.Element {
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { position, isPositioned } = useDynamicPopupPosition({
    triggerRef,
    popupRef: panelRef,
    isVisible: isOpen && useDynamicPosition,
    preferredPosition,
    offset: 8,
    viewportPadding: 16
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function handlePointerUp(e: PointerEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) {
        return;
      }
      setIsOpen(false);
      onClose?.();
      document.removeEventListener('pointerup', handlePointerUp);
    }
    document.addEventListener('pointerup', handlePointerUp);
    return () => document.removeEventListener('pointerup', handlePointerUp);
  }, [isOpen, onClose]);

  const openPicker = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onOpen) {
        onOpen();
      } else {
        setIsOpen(true);
      }
    },
    [onOpen]
  );

  // Composes the font-select callback with navigation side-effects so that
  // BrandKitFontPickerContent stays unaware of inline vs floating-panel context.
  const handleSelect = useCallback(
    (url: string, name: string) => {
      onFontSelect(url, name);
      if (inline) {
        onBack?.();
      } else {
        setIsOpen(false);
      }
    },
    [onFontSelect, inline, onBack]
  );

  if (inline) {
    return (
      <div className="p-3">
        <div className="mb-2 flex items-center gap-1">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1 rounded-md p-1 text-slate-700 hover:bg-slate-100 hover:text-slate-900"
            >
              <Icon name="IconChevronLeft" size={16} />
            </button>
          )}
          <h2 className="text-sm font-semibold text-slate-950">{title}</h2>
        </div>
        <BrandKitFontPickerContent
          selectedFontUrl={selectedFontUrl}
          selectedFontDisplayName={selectedFontDisplayName}
          onSelect={handleSelect}
        />
      </div>
    );
  }

  return (
    <>
      <div
        ref={triggerRef}
        data-testid="brand-kit-font-picker-trigger"
        className={classnames(
          'flex size-full cursor-pointer items-center border border-slate-200',
          classNames,
          isOpen ? openClassNames : ''
        )}
        onClick={openPicker}
      >
        {children}
      </div>

      {isOpen && (
        <div
          ref={panelRef}
          data-testid="brand-kit-font-picker-panel"
          className={classnames(useDynamicPosition ? 'fixed' : 'absolute', 'z-50', panelClassNames)}
          style={{
            ...(useDynamicPosition ? position : {}),
            opacity: useDynamicPosition ? (isPositioned ? 1 : 0) : 1,
            transition: 'opacity 150ms ease-in-out'
          }}
        >
          <div className="w-[min(100vw-2rem,300px)] rounded-lg border border-slate-200 bg-white p-3 shadow-xl">
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-sm font-semibold text-slate-950">{title}</h2>
            </div>
            <BrandKitFontPickerContent
              selectedFontUrl={selectedFontUrl}
              selectedFontDisplayName={selectedFontDisplayName}
              onSelect={handleSelect}
            />
          </div>
        </div>
      )}
    </>
  );
}
